import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, CheckCircle2, Clock, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import AIQuickHelp from "@/components/AIQuickHelp";
import PhoneInput, { formatPhone } from "@/components/PhoneInput";
import usePageMeta from "@/hooks/usePageMeta";

const COUNTRIES = ["India", "United States", "United Kingdom", "Germany", "Singapore", "United Arab Emirates", "Australia", "Japan", "South Korea", "China", "Brazil", "Canada", "Other"];
const INDUSTRIES = ["Chemicals", "Specialty Chemicals", "Packaging", "Semiconductors", "Energy Transition", "Mining", "Advanced Materials", "Industrial Automation", "Manufacturing", "Sustainability", "Circular Economy", "Bio-Based Materials", "Other"];
const SERVICES = ["Market Intelligence", "Competitive Intelligence", "Strategic Advisory", "Growth Strategy", "Market Assessment", "Customer Intelligence", "Technology Intelligence", "Due Diligence", "Custom Research"];

export function ContactPage() {
  usePageMeta({
    title: "Contact Luminary — Strategic Intelligence & Advisory",
    description: "Inquiries from operating leaders, investors, and media. Our practice desk responds within one business day. Or ask our AI Quick Help concierge.",
    path: "/contact",
    keywords: "contact Luminary, strategic advisory inquiry, decision intelligence",
  });

  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    company_name: "", contact_name: "", designation: "", email: "",
    country: "India", industry: "Chemicals", service: "Market Intelligence", message: "",
  });
  const [phone, setPhone] = useState({ iso: "IN", code: "+91", number: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.number.trim()) {
      setErr("Please enter a valid phone number with country code.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const payload = { ...form, phone: formatPhone(phone) };
      const { data } = await api.post("/contact-v3", payload);
      setDone(data);
    } catch (e2) { setErr(formatApiError(e2)); }
    setBusy(false);
  };

  return (
    <PublicLayout>
      {!done ? (
        <section className="border-b border-[var(--border)] bg-[var(--bg)] py-12 sm:py-20" data-testid="contact-page">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="eyebrow">Contact</div>
              <h1 className="font-display font-black text-[clamp(2.4rem,5.4vw,4.4rem)] mt-4 leading-[0.95] tracking-tight text-[var(--navy)]">
                Start a<br/><span className="italic font-medium text-[var(--gold)]">conversation.</span>
              </h1>
              <p className="mt-6 font-display italic text-xl text-[var(--slate)] max-w-xl">
                Inquiries from operating leaders, investors, and media. We respond within one business day.
              </p>

              {/* AI Quick Help replaces the office block */}
              <div className="mt-9">
                <AIQuickHelp />
              </div>

              <div className="mt-9 grid sm:grid-cols-2 gap-5">
                <div className="border-l-2 border-[var(--gold)] pl-4">
                  <div className="eyebrow">Direct</div>
                  <p className="mt-3 text-sm text-[var(--navy)]">
                    <Mail size={12} className="inline mr-2 text-[var(--gold)]" />
                    <a
                      href={`mailto:${settings?.contact_email || "support@luminaryin.com"}`}
                      className="hover:text-[var(--gold)]"
                      data-testid="contact-direct-email"
                    >
                      {settings?.contact_email || "support@luminaryin.com"}
                    </a>
                  </p>
                </div>
                <div className="border-l-2 border-[var(--gold)] pl-4">
                  <div className="eyebrow">Response</div>
                  <p className="mt-3 text-sm text-[var(--navy)]">
                    <Clock size={12} className="inline mr-2 text-[var(--gold)]" />
                    Within one business day.
                  </p>
                </div>
              </div>

              <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--slate-light)] flex items-center gap-2">
                <ShieldCheck size={12} className="text-[var(--gold)]" />
                Remote-first practice · Operating globally across 14 industries.
              </p>
            </div>

            <form onSubmit={submit} className="lg:col-span-7 bg-white border border-[var(--border)] p-6 sm:p-8" data-testid="contact-form">
              <div className="eyebrow">Inquire</div>
              <h2 className="font-display font-black text-3xl mt-3 text-[var(--navy)]">Tell us what you need.</h2>
              <p className="mt-2 text-sm text-[var(--slate-light)]">All inquiries land in our practice inbox and route to support@luminaryin.com.</p>

              <div className="mt-7 grid sm:grid-cols-2 gap-5">
                <div><label className="label-mono">Company name *</label><input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="input-editorial" data-testid="contact-company" /></div>
                <div><label className="label-mono">Contact person *</label><input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="input-editorial" data-testid="contact-name" /></div>
                <div><label className="label-mono">Designation *</label><input required value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="input-editorial" placeholder="e.g., Chief Strategy Officer" data-testid="contact-designation" /></div>
                <div><label className="label-mono">Work email *</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-editorial" data-testid="contact-email" /></div>
                <div>
                  <PhoneInput
                    label="Phone (with country code)"
                    required
                    value={phone}
                    onChange={setPhone}
                    testIdPrefix="contact-phone"
                  />
                </div>
                <div><label className="label-mono">Country *</label>
                  <select required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input-editorial" data-testid="contact-country">
                    {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="label-mono">Industry *</label>
                  <select required value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="input-editorial" data-testid="contact-industry">
                    {INDUSTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="label-mono">Service needed *</label>
                  <select required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input-editorial" data-testid="contact-service">
                    {SERVICES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="label-mono">Brief description (optional)</label>
                <textarea
                  rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Anything else we should know about your strategic objective."
                  className="input-editorial resize-none" data-testid="contact-message"
                />
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <button type="submit" disabled={busy} className="btn-primary" data-testid="contact-submit-button">
                  {busy ? "Sending…" : <><Send size={14} /> Send inquiry</>}
                </button>
                {err && <p className="text-sm font-mono text-red-700">{err}</p>}
              </div>
            </form>
          </div>
        </section>
      ) : (
        <section className="border-b border-[var(--border)] bg-[var(--bg)] py-20 sm:py-28">
          <div className="max-w-[800px] mx-auto px-4 sm:px-6 text-center" data-testid="contact-confirmation">
            <CheckCircle2 size={44} className="mx-auto text-[var(--success)]" />
            <div className="eyebrow mt-6 text-[var(--success)]">Inquiry received</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl mt-4 leading-tight text-[var(--navy)]">Thank you.</h1>
            <p className="mt-5 text-lg text-[var(--slate)]">Your inquiry has been routed to our practice inbox. We will respond within one business day.</p>
            <p className="mt-3 text-sm font-mono uppercase tracking-[0.2em] text-[var(--slate-light)]">Reference: {done.id}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/" className="btn-primary">Back to home</Link>
              <Link to="/trackers" className="btn-secondary">Explore intelligence trackers</Link>
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}

export function EnterprisePage() {
  return <ContactPage />;
}
